import { LoadingOutlined } from '@ant-design/icons';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useIdleTimer } from 'react-idle-timer';
import styled from 'styled-components';

import theme from '../../assets/theme';
import { useDesignerDataContext } from '../../hooks/useDesignerLogic';
import {
  DesignerUIStateActions,
  useDesignerUIContext,
} from '../../hooks/useDesignerUIState';
import { gameCacheKey } from '../../hooks/useGame';
import { NewRelic } from '../../hooks/useNewRelic';
import useRouterMatch from '../../hooks/useRouterMatch';
import { zIndexes } from '../../utils/zIndexes';
const FIVE_MIN_MS = 5 * 60 * 1000;
const isWindowListenerSupported =
  typeof window !== 'undefined' && window.addEventListener;
const isBroadcastChannelSupported = typeof BroadcastChannel !== 'undefined';

const DesginerMultiTabHandlerProvider = ({
  children,
}: {
  children: React.ReactChild;
}) => {
  const { templateId } = useRouterMatch();

  const client = useQueryClient();
  const [designerActiveInAnotherTab, setDesignerActiveInAnotherTab] = useState(false);
  const [isPageVisible, setIsPageVisible] = useState(true);

  const dispatch = useDesignerUIContext(context => context.dispatch);
  const localTemplate = useDesignerDataContext(context => context.state.localTemplate);
  const forceLoadingState = useDesignerUIContext(c => c.state.forceLoadingState);
  const isUserIdle = useDesignerUIContext(c => c.state.isUserIdle);
  const deselectElements = useDesignerUIContext(c => c.deselectElements);

  const activeDesignerTabBroadcastChannel = useMemo(
    () =>
      isBroadcastChannelSupported ? new BroadcastChannel('active-designer-tab') : null,
    [],
  );

  const onUserActive = useCallback(() => {
    dispatch({
      type: DesignerUIStateActions.setForceLoadingState,
      payload: 'loading',
    });
    client.invalidateQueries(['template', templateId]);
    client.invalidateQueries([gameCacheKey, localTemplate?.content.activeGame?.id]);
    dispatch({ type: DesignerUIStateActions.setIsUserIdle, payload: false });
    deselectElements();
  }, [
    client,
    dispatch,
    localTemplate?.content.activeGame?.id,
    templateId,
    deselectElements,
  ]);

  useIdleTimer({
    onActive: onUserActive,
    onIdle: () => dispatch({ type: DesignerUIStateActions.setIsUserIdle, payload: true }),
    timeout: FIVE_MIN_MS, //timeout to consider user as idle
  });

  useEffect(() => {
    if (!templateId) return;
    if (activeDesignerTabBroadcastChannel) {
      try {
        activeDesignerTabBroadcastChannel?.postMessage(templateId);
        activeDesignerTabBroadcastChannel.onmessage = event => {
          if (event.data === templateId) {
            setDesignerActiveInAnotherTab(true);
          }
        };
      } catch (error) {
        NewRelic()?.noticeError(error);
      }
    }

    return () => {
      try {
        activeDesignerTabBroadcastChannel?.close();
      } catch (error) {
        NewRelic()?.noticeError(error);
      }
    };
  }, [templateId, activeDesignerTabBroadcastChannel]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      setIsPageVisible(document.visibilityState === 'visible');
    };

    if (isWindowListenerSupported) {
      window.addEventListener('visibilitychange', handleVisibilityChange, false);
      return () => {
        window.removeEventListener('visibilitychange', handleVisibilityChange, false);
      };
    }
  }, []);

  useEffect(() => {
    try {
      if (isPageVisible && designerActiveInAnotherTab) {
        activeDesignerTabBroadcastChannel?.postMessage(templateId);
        setDesignerActiveInAnotherTab(false);
        onUserActive();
      }
    } catch (error) {
      NewRelic()?.noticeError(error);
    }
  }, [
    isPageVisible,
    activeDesignerTabBroadcastChannel,
    designerActiveInAnotherTab,
    onUserActive,
    templateId,
  ]);

  return (
    <>
      {forceLoadingState === 'loading' || designerActiveInAnotherTab || isUserIdle ? (
        <OverlayMask>
          <StyledLoadingOutlined />
        </OverlayMask>
      ) : null}
      {children}
    </>
  );
};

export default DesginerMultiTabHandlerProvider;

const OverlayMask = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  position: absolute;
  width: 100%;
  height: 100%;
  background: #ffffff;
  opacity: 0.7;
  z-index: ${zIndexes('FULL_SIZE_MODAL')};
`;

const StyledLoadingOutlined = styled(LoadingOutlined)`
  font-size: 60px;
  color: ${theme.colors.black};
`;
